import { Plus } from "lucide-react";

interface StatCardProps {
  type: "feed" | "sleep" | "diaper" | "growth" | "medicine" | "meal" | "water" | "milk";
  icon: React.ReactNode;
  title: string;
  value: React.ReactNode;
  onClick?: () => void;
  onAddClick?: () => void;
}

// Old design: full-height colored left bar + white right side with bold value.
// barBg is the saturated brand color used for the full vertical bar.
// addBg is the slightly darker shade used for the small "+" circle inside the bar.
const TYPE_STYLE: Record<string, { barBg: string; addBg: string; labelColor: string }> = {
  feed:     { barBg: 'hsl(20, 95%, 65%)',  addBg: 'hsl(20, 95%, 55%)',  labelColor: 'hsl(var(--feed-accent))'     },
  sleep:    { barBg: 'hsl(260, 75%, 70%)', addBg: 'hsl(260, 75%, 60%)', labelColor: 'hsl(var(--sleep-accent))'    },
  diaper:   { barBg: 'hsl(200, 85%, 65%)', addBg: 'hsl(200, 85%, 55%)', labelColor: 'hsl(var(--diaper-accent))'   },
  growth:   { barBg: 'hsl(335, 80%, 65%)', addBg: 'hsl(335, 80%, 55%)', labelColor: 'hsl(var(--growth-accent))'   },
  medicine: { barBg: 'hsl(200, 85%, 65%)', addBg: 'hsl(200, 85%, 55%)', labelColor: 'hsl(var(--medicine-accent))' },
  meal:     { barBg: 'hsl(20, 95%, 65%)',  addBg: 'hsl(20, 95%, 55%)',  labelColor: 'hsl(var(--feed-accent))'     },
  water:    { barBg: 'hsl(200, 85%, 65%)', addBg: 'hsl(200, 85%, 55%)', labelColor: 'hsl(var(--medicine-accent))' },
  milk:     { barBg: 'hsl(335, 80%, 65%)', addBg: 'hsl(335, 80%, 55%)', labelColor: 'hsl(var(--growth-accent))'   },
};

const StatCard = ({
  type, icon, title, value, onClick, onAddClick,
}: StatCardProps) => {
  const s = TYPE_STYLE[type];

  return (
    <div
      className={`bg-white rounded-2xl flex items-stretch overflow-hidden${onClick ? ' cursor-pointer active:scale-[0.98] transition-transform' : ''}`}
      style={{ boxShadow: 'var(--shadow-card)' }}
      onClick={onClick}
    >
      {/* Left: full-height saturated colored bar with icon on top + small "+" below */}
      <div
        className="flex flex-col items-center justify-center gap-2 px-3 py-3 flex-shrink-0"
        style={{ background: s.barBg, minWidth: 52 }}
      >
        <span style={{ fontSize: 20, lineHeight: 1 }}>{icon}</span>

        {onAddClick ? (
          <button
            onClick={(e) => { e.stopPropagation(); onAddClick(); }}
            className="w-5 h-5 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ background: s.addBg }}
          >
            <Plus className="w-3 h-3 text-white" strokeWidth={3} />
          </button>
        ) : (
          <div className="w-5 h-5" />
        )}
      </div>

      {/* Right: label (small uppercase) + bold large value */}
      <div className="flex-1 min-w-0 px-3 py-3 flex flex-col justify-center">
        <p
          className="whitespace-nowrap"
          style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'hsl(var(--muted-foreground))' }}
        >
          {title}
        </p>
        <div
          className="whitespace-nowrap mt-1"
          style={{
            // Drop font size when the value text is long so it never gets clipped.
            // 22px fits "0h" / "6kg"; 18px fits "10/12" / "1200ml"; 16px fits longer
            // edge cases without ever ellipsising.
            fontSize: typeof value === 'string' && value.length > 6 ? 16 : typeof value === 'string' && value.length > 4 ? 18 : 22,
            fontWeight: 800,
            color: 'hsl(var(--foreground))',
            lineHeight: 1.1,
          }}
        >
          {value}
        </div>
      </div>
    </div>
  );
};

export default StatCard;
